import { Navigate, Routes, Route, Outlet } from "react-router-dom";
import { lazy } from "react";

import { ProtectedRoute } from "./ProtectedRoute";
import Unauthorized from "@/errors/Unauthorized";
import NotFound from "@/errors/NotFound";
import { useAppTheme } from "@/common/hooks/useAppTheme";
import {
	useSessionContext,
	RoleType
} from "kmstack-shared";
import { Page } from "@/types";
import { DashboardPage } from "./DashboardPage";
import LoadingSpinner from "@/components/LoadingSpinner";
import ServerError from "@/errors/ServerError";
import { AuthService } from './auth.service';
import { getUserMembershipForActiveOrg } from "@/common/utils/transformers";
import { FeatureFlag } from "kmstack-shared/apps/accounting";

// Lazy load route components
const ClientSupplierView = lazy(() => import("@/components/dashboard/clients-suppliers/ClientSupplierView").then(m => ({ default: m.ClientSupplierView })));
const DashBoardHome = lazy(() => import("./pages/DashBoardHome").then(m => ({ default: m.DashBoardHome })));
const WorkforceManagementView = lazy(() => import("@/components/dashboard/workforce/WorkforceManagementView"));
const InvoicesView = lazy(() => import("@/components/dashboard/invoices/InvoicesView"));
const AutomationView = lazy(() => import("@/components/dashboard/AutomationView"));
const PurchasesView = lazy(() => import("@/components/dashboard/purchases/PurchasesView"));
const BillsExpenseReportView = lazy(() => import("@/components/dashboard/purchases/BillsExpenseReport").then(m => ({ default: m.BillsExpenseReportView })));
const ExpenseTracking = lazy(() => import("@/components/dashboard/purchases/expenses/ExpenseTracking").then(m => ({ default: m.ExpenseTracking })));
const BillTracking = lazy(() => import("@/components/dashboard/purchases/bills/BillTracking").then(m => ({ default: m.BillTracking })));
const IntegrationsView = lazy(() => import("@/components/dashboard/IntegrationsView"));
const InventoryView = lazy(() => import("@/components/dashboard/inventory/InventoryView"));
const PaymentsView = lazy(() => import("@/components/dashboard/PaymentsView"));
const SettingsView = lazy(() => import("@/components/dashboard/SettingsView"));
const SecurityComplianceView = lazy(() => import("@/components/dashboard/SecurityComplianceView"));
const TimeTrackingView = lazy(() => import("@/components/dashboard/workforce/time-tracking/TimeTrackingView").then(m => ({ default: m.TimeTrackingView })));
const TimerTab = lazy(() => import("@/components/dashboard/workforce/time-tracking/TimerTab").then(m => ({ default: m.TimerTab })));
const TimeSheetsTab = lazy(() => import("@/components/dashboard/workforce/time-tracking/TimeSheetsTab"));
const ProjectsTab = lazy(() => import("@/components/dashboard/workforce/time-tracking/ProjectsTab").then(m => ({ default: m.ProjectsTab })));
const InvoicingTab = lazy(() => import("@/components/dashboard/invoices/InvoicingTab"));
const PayrollProcessingView = lazy(() => import("@/components/dashboard/workforce/payroll/PayrollProcessingView"));
const EmployeePortalView = lazy(() => import("@/components/dashboard/workforce/employee-portal/EmployeePortalExperience"));
const PayrollSettingsView = lazy(() => import("@/components/dashboard/workforce/payroll/PayrollSettingsView"));
const PayslipsView = lazy(() => import("@/components/dashboard/PayslipsView"));
const LeaveApprovalsView = lazy(() => import("@/components/dashboard/workforce/leave-approvals/LeaveApprovalsView").then(m => ({ default: m.LeaveApprovalsView })));
const InvoiceSettings = lazy(() => import("@/components/dashboard/settings/invoice/InvoiceSettings").then(m => ({ default: m.InvoiceSettings })));
const SettingNotification = lazy(() => import("@/components/dashboard/settings/notification/SettingNotification").then(m => ({ default: m.SettingNotification })));
const PaymentsMethodsSettings = lazy(() => import("@/components/dashboard/settings/payments/PaymentMethods.setting").then(m => ({ default: m.PaymentsMethodsSettings })));
const UserRolePermission = lazy(() => import("@/components/dashboard/settings/permissions/UserRolePermission").then(m => ({ default: m.UserRolePermission })));
const TransactionSettings = lazy(() => import("@/components/dashboard/settings/transaction-settings/TransactionSettings").then(m => ({ default: m.TransactionSettings })));
const APIIntegration = lazy(() => import("@/components/dashboard/integrations/APIIntegration").then(m => ({ default: m.APIIntegration })));
const MarketPlaceIntegration = lazy(() => import("@/components/dashboard/integrations/MarketplaceIntegration").then(m => ({ default: m.MarketPlaceIntegration })));
const WebhooksIntegration = lazy(() => import("@/components/dashboard/integrations/WebhooksIntegration").then(m => ({ default: m.WebhooksIntegration })));
const CompliancePrivacySettings = lazy(() => import("@/components/dashboard/security/CompliancePrivacySettings").then(m => ({ default: m.CompliancePrivacySettings })));
const SmartPaymentReminders = lazy(() => import("@/components/dashboard/automations/SmartPaymentReminders").then(m => ({ default: m.SmartPaymentReminders })));
const AutoCategorizations = lazy(() => import("@/components/dashboard/automations/AutoCategorization").then(m => ({ default: m.AutoCategorizations })));
const TaxReports = lazy(() => import("@/components/dashboard/automations/TaxReports").then(m => ({ default: m.TaxReports })));
const InventoryReportsView = lazy(() => import("@/components/dashboard/inventory/helpers/InventoryReportsView"));
const PurchaseOrdersView = lazy(() => import("@/components/dashboard/inventory/PurchaseOrdersView"));
const InventoryManagementView = lazy(() => import("@/components/dashboard/inventory/InventoryManagementView").then(m => ({ default: m.InventoryManagementView })));
const PaymentTrackingTab = lazy(() => import("@/components/dashboard/payments/PaymentTrackingView").then(m => ({ default: m.PaymentTrackingTab })));
const CryptoPaymentsView = lazy(() => import("@/components/dashboard/payments/CryptoPaymentsView").then(m => ({ default: m.CryptoPaymentsView })));
const EmployeesList = lazy(() => import("@/components/dashboard/workforce/employees/EmployeeList").then(m => ({ default: m.EmployeesList })));
const AllReportsView = lazy(() => import("@/components/dashboard/reports/AllReports").then(m => ({ default: m.AllReportsView })));
const FinancialTaxReportsView = lazy(() => import("@/components/dashboard/reports/FinancialTaxReports").then(m => ({ default: m.FinancialTaxReportsView })));
const OperationsReportsView = lazy(() => import("@/components/dashboard/reports/OperationsReports").then(m => ({ default: m.OperationsReportsView })));
const AnalyticsForecastingView = lazy(() => import("@/components/dashboard/reports/AnalyticsForecasting").then(m => ({ default: m.AnalyticsForecastingView })));
// const DataSecurityDashboard = lazy(() => import("@/components/dashboard/compliance/Ds"));
// const ComplianceDashboard = lazy(() => import("@/components/dashboard/compliance/ComplianceDashboard"));
// const PaymentsSecurityDashboard = lazy(() => import("@/components/dashboard/compliance/Sars"));

const managementRoles = [
	RoleType.ACCOUNT_ADMIN,
	RoleType.ACCOUNT_ACCOUNTANT,
	RoleType.ACCOUNT_HR
];

const employeePortalRoles = [
	RoleType.ACCOUNT_EMPLOYEE
];

const employeePortalFeatures = [
	FeatureFlag.KmstackEmployeePayslipPortal,
];
const dashboardFeatures = [FeatureFlag.KmstackDashboard];
const clientSupplierFeatures = [FeatureFlag.KmstackClientManagement, FeatureFlag.KmstackABClientPortal];
const workforceOperationRoles = [RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR];
const workforceFeatures = [
	FeatureFlag.KmstackEmployeeManagement,
	FeatureFlag.KmstackABTimesheet,
	FeatureFlag.KmstackLeaveAndAbsenceTracking,
	FeatureFlag.KmstackABPayslip,
	FeatureFlag.KmstackABPayroll,
];
const employeeManagementFeatures = [FeatureFlag.KmstackEmployeeManagement];
const timeTrackingFeatures = [FeatureFlag.KmstackABTimesheet];
const projectFeatures = [FeatureFlag.KmstackABProject];
const leaveAbsenceFeatures = [FeatureFlag.KmstackLeaveAndAbsenceTracking];
const payrollFeatures = [FeatureFlag.KmstackABPayroll];
const payslipFeatures = [FeatureFlag.KmstackABPayslip];
const invoiceFeatures = [FeatureFlag.KmstackABInvoice];
const purchaseFeatures = [FeatureFlag.KmstackABExpense, FeatureFlag.KmstackBillTracking];
const billTrackingFeatures = [FeatureFlag.KmstackBillTracking];
const purchaseReportFeatures = [FeatureFlag.KmstackReportingAdvanced, FeatureFlag.KmstackDataExport];
const paymentFeatures = [FeatureFlag.KmstackABInvoicePayment, FeatureFlag.KmstackABPartialPayment, FeatureFlag.KmstackABCryptoPayment, FeatureFlag.KmstackABCryptoWallet];
const cryptoPaymentFeatures = [FeatureFlag.KmstackABCryptoPayment, FeatureFlag.KmstackABCryptoWallet];
const inventoryFeatures = [FeatureFlag.KmstackABInventory];
const inventoryPurchaseOrderFeatures = [FeatureFlag.KmstackBillTracking];
const inventoryReportFeatures = [FeatureFlag.KmstackReportingAdvanced, FeatureFlag.KmstackDataExport];
const reportFeatures = [
	FeatureFlag.KmstackABVATReport,
	FeatureFlag.KmstackABTaxFiling,
	FeatureFlag.KmstackABTaxReport,
	FeatureFlag.KmstackABFinancialReport,
	FeatureFlag.KmstackReportingAdvanced,
	FeatureFlag.KmstackDataExport,
];
const financialTaxReportFeatures = [
	FeatureFlag.KmstackABVATReport,
	FeatureFlag.KmstackABTaxFiling,
	FeatureFlag.KmstackABTaxReport,
	FeatureFlag.KmstackABFinancialReport,
	FeatureFlag.KmstackABChartOfAccounts,
	FeatureFlag.KmstackABGeneralLedger,
];
const advancedReportFeatures = [FeatureFlag.KmstackReportingAdvanced];
const automationFeatures = [
	FeatureFlag.KmstackABPaymentReminder,
	FeatureFlag.KmstackABInvoiceCategoryPrediction,
	FeatureFlag.KmstackAIForecasting,
	FeatureFlag.KmstackSmartContracts,
	FeatureFlag.KmstackABVATReport,
	FeatureFlag.KmstackABTaxFiling,
];
const settingsFeatures = [
	FeatureFlag.KmstackInvoiceTemplates,
	FeatureFlag.KmstackNotificationSystem,
	FeatureFlag.KmstackAutoTax,
	FeatureFlag.KmstackOrgSettings,
	FeatureFlag.KmstackUserManagement,
	FeatureFlag.KmstackRolePermissionManagement,
];
const integrationFeatures = [FeatureFlag.KmstackAPIManagement, FeatureFlag.KmstackWebhookConfig, FeatureFlag.KmstackPluginManagement];
const securityFeatures = [FeatureFlag.KmstackDataPrivacyCompliance, FeatureFlag.KmstackAuditLogs, FeatureFlag.KmstackABFraudAlert];

function DashboardLayout() {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const navigateTo = (_page: Page, _email?: string) => { };
	return (
		<DashboardPage onNavigate={navigateTo}>
			<Outlet />
		</DashboardPage>
	);
}

export default function AppRouter() {

	// const  { trackEvent } = useTracking()
	// trackEvent("button_click", { label: "submit" });	

	// we should get user data here and store in redux if not already there(use the method as used in other application)
	// only after the data is valid can we continue, so basically we should be showing loading until we receieve data
	// Get user data from React Query
	useAppTheme();
	const { sessionContext, isLoading, isAuthenticated } = useSessionContext();

	if (isLoading || AuthService.isLoggingOut) {
		return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
	}

	if (!isAuthenticated || !sessionContext) {
		// If not loading but not authenticated or no sessionContext, something went wrong (or unauthorized)
		return <ServerError />;
	}


	const membership = getUserMembershipForActiveOrg(sessionContext);
	if (!membership) {
		return <ServerError />;
	}
	const userRole = membership.role?.name;
	if (!userRole) {
		return <ServerError />;
	}
	const userPlan = membership.planType;



	return (
		<Routes>
			<Route
				path="/org/employee-portal/*"
				element={
					<ProtectedRoute allowedRoles={employeePortalRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={employeePortalFeatures}>
						<EmployeePortalView />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/org"
				element={
					userRole === RoleType.ACCOUNT_EMPLOYEE
						? <Navigate to="/org/employee-portal" replace />
						: <Navigate to="/" replace />
				}
			/>
			<Route element={<DashboardLayout />}>
				<Route
					path="/org/clients-suppliers"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={clientSupplierFeatures}>
							<ClientSupplierView />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/org/workforce"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={workforceFeatures}>
							<WorkforceManagementView />
						</ProtectedRoute>
					}
				>
					<Route
						path="users"
						element={
							<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={employeeManagementFeatures}>
								<EmployeesList />
							</ProtectedRoute>
						}
					/>
					<Route
						path="time-tracking"
						element={
							<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={timeTrackingFeatures}>
								<TimeTrackingView />
							</ProtectedRoute>
						}
					>
						<Route
							path="timer"
							element={
								<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={timeTrackingFeatures}>
									<TimerTab />
								</ProtectedRoute>
							}
						/>
						<Route
							path="projects"
							element={
								<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={projectFeatures}>
									<ProjectsTab />
								</ProtectedRoute>
							}
						/>
						<Route
							path="time-sheets"
							element={
								<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={timeTrackingFeatures}>
									<TimeSheetsTab />
								</ProtectedRoute>
							}
						/>
						<Route
							path="invoicing"
							element={
								<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={timeTrackingFeatures}>
									<InvoicingTab />
								</ProtectedRoute>
							}
						/>
					</Route>
					<Route
						path="leave-approvals"
						element={
							<ProtectedRoute allowedRoles={workforceOperationRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={leaveAbsenceFeatures}>
								<LeaveApprovalsView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="payroll-processing"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={payrollFeatures}>
								<PayrollProcessingView />
							</ProtectedRoute>
						}
					>
					</Route>
					<Route
						path="payslips"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={payslipFeatures}>
								<PayslipsView />
							</ProtectedRoute>
						}
					>
					</Route>
					<Route
						path="employee-portal"
						element={
							<Navigate to="/org/employee-portal" replace />
						}
					>
					</Route>
					<Route
						path="payroll-settings"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={payrollFeatures}>
								<PayrollSettingsView />
							</ProtectedRoute>
						}
					>
					</Route>
				</Route>
				<Route
					path="/org/invoices"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={invoiceFeatures}>
							<InvoicesView />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/org/purchases"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={purchaseFeatures}>
							<PurchasesView />
						</ProtectedRoute>
					}
				>
					<Route
						path="expenses"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABExpense]}>
								<ExpenseTracking />
							</ProtectedRoute>
						}
					/>
					<Route
						path="bills"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={billTrackingFeatures}>
								<BillTracking />
							</ProtectedRoute>
						}
					/>
					<Route
						path="reports"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={purchaseReportFeatures}>
								<BillsExpenseReportView />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/payments"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={paymentFeatures}>
							<PaymentsView />
						</ProtectedRoute>
					}
				>
					<Route
						path="tracking"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABInvoicePayment]}>
								<PaymentTrackingTab />
							</ProtectedRoute>
						}
					/>
					<Route
						path="crypto"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={cryptoPaymentFeatures}>
								<CryptoPaymentsView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="partial-bnpl"
						element={<Navigate to="/org/payments/tracking" replace />}
					/>
				</Route>
				<Route
					path="/org/inventory"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={inventoryFeatures}>
							<InventoryView />
						</ProtectedRoute>
					}
				>
					<Route
						path="management"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={inventoryFeatures}>
								<InventoryManagementView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="purchase-orders"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={inventoryPurchaseOrderFeatures}>
								<PurchaseOrdersView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="reports"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={inventoryReportFeatures}>
								<InventoryReportsView />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/report-analytics"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={reportFeatures}>
							<AllReportsView />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/org/report-analytics/reports"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN]} userRole={userRole} userPlan={userPlan} requiredFeatures={reportFeatures}>
							<AllReportsView />
						</ProtectedRoute>
					}
				>
					<Route
						path="financial-tax"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={financialTaxReportFeatures}>
								<FinancialTaxReportsView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="operations"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={advancedReportFeatures}>
								<OperationsReportsView />
							</ProtectedRoute>
						}
					/>
					<Route
						path="analytics-forecasting"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackAIForecasting]}>
								<AnalyticsForecastingView />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/automation"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={automationFeatures}>
							<AutomationView />
						</ProtectedRoute>
					}
				>
					<Route
						path="smart-payment-reminders"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABPaymentReminder]}>
								<SmartPaymentReminders />
							</ProtectedRoute>
						}
					/>
					<Route
						path="auto-categorization"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABInvoiceCategoryPrediction]}>
								<AutoCategorizations />
							</ProtectedRoute>
						}
					/>
					<Route
						path="tax-reports"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABVATReport, FeatureFlag.KmstackABTaxFiling, FeatureFlag.KmstackABTaxReport]}>
								<TaxReports />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/integrations"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN]} userRole={userRole} userPlan={userPlan} requiredFeatures={integrationFeatures}>
							<IntegrationsView />
						</ProtectedRoute>
					}
				>
					<Route
						path="webhooks"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackWebhookConfig]}>
								<WebhooksIntegration />
							</ProtectedRoute>
						}
					/>
					<Route
						path="api"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackAPIManagement]}>
								<APIIntegration />
							</ProtectedRoute>
						}
					/>
					<Route
						path="marketplace"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackPluginManagement]}>
								<MarketPlaceIntegration />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/settings"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={settingsFeatures}>
							<SettingsView />
						</ProtectedRoute>
					}
				>
					<Route
						path="invoice-settings"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackInvoiceTemplates]}>
								<InvoiceSettings />
							</ProtectedRoute>
						}
					/>
					<Route
						path="payment-methods"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackABInvoicePayment, FeatureFlag.KmstackABPartialPayment]}>
								<PaymentsMethodsSettings />
							</ProtectedRoute>
						}
					/>
					<Route
						path="notification"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackNotificationSystem]}>
								<SettingNotification />
							</ProtectedRoute>
						}
					/>
					<Route
						path="user-roles-permissions"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackUserManagement, FeatureFlag.KmstackRolePermissionManagement, FeatureFlag.KmstackCustomRoles]}>
								<UserRolePermission />
							</ProtectedRoute>
						}
					/>
					<Route
						path="transaction-settings"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT, RoleType.ACCOUNT_HR]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackAutoTax, FeatureFlag.KmstackABChartOfAccounts]}>
								<TransactionSettings />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path="/org/security-compliance"
					element={
						<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={securityFeatures}>
							<SecurityComplianceView />
						</ProtectedRoute>
					}
				>
					{/* <Route
						path="dashboard"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole}>
								<ComplianceDashboard />
							</ProtectedRoute>
						}
					/> */}
					{/* <Route
						path="data-security"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole}>
								<DataSecurityDashboard />
							</ProtectedRoute>
						}
					/> */}
					{/* <Route
						path="payment-security"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole}>
								<PaymentsSecurityDashboard />
							</ProtectedRoute>
						}
					/> */}

					<Route
						path="compliance-privacy"
						element={
							<ProtectedRoute allowedRoles={[RoleType.ACCOUNT_ADMIN, RoleType.ACCOUNT_ACCOUNTANT]} userRole={userRole} userPlan={userPlan} requiredFeatures={[FeatureFlag.KmstackDataPrivacyCompliance]}>
								<CompliancePrivacySettings />
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route path="/unauthorized" element={<Unauthorized />} />
				<Route path="/" element={
					userRole === RoleType.ACCOUNT_EMPLOYEE
						? <Navigate to="/org/employee-portal" replace />
						: (
							<ProtectedRoute allowedRoles={managementRoles} userRole={userRole} userPlan={userPlan} requiredFeatures={dashboardFeatures}>
								<DashBoardHome />
							</ProtectedRoute>
						)
				}
				/>
				<Route path="*" element={<NotFound />} />
			</Route>
		</Routes>
	);
}
